import { navigatorDebugObject } from "./navigatorDebug";
import BaseObject from "../../models/BaseObject";
import type BaseModel from "../../models/BaseModel";
import TreeNodeInfo from "../../models/TreeNodeInfo";

export type NavigatorChildren = (node: TreeNodeInfo) => readonly TreeNodeInfo[];

/** Keeps short omitted runs visible; a break must replace at least three siblings. */
function includeShortSiblingGaps(onDisplayPath: readonly boolean[]): boolean[] {
  const visible = [...onDisplayPath];
  if (!visible.some(Boolean)) return visible;
  let index = 0;
  while (index < visible.length) {
    if (visible[index]) { index++; continue; }
    const start = index;
    while (index < visible.length && !visible[index]) index++;
    if (index - start < 3) {
      for (let sibling = start; sibling < index; sibling++) visible[sibling] = true;
    }
  }
  return visible;
}

/** One occurrence of an object in the tree, independent of its mounted React row. */
export class NavigatorNode extends BaseObject {
  get className(): string { return "NavigatorNode"; }
  readonly cachedChildren = new Map<string, NavigatorNode>();
  visibleChildren: readonly NavigatorNode[] = [];
  expanded = false;
  partial = false;
  isPartiallyExpanded = false;
  hasHiddenPredecessor = false;
  hasHiddenSuccessors = false;
  private subscription?: string;

  constructor(readonly model: NavigatorModel, readonly info: TreeNodeInfo, readonly parentNode?: NavigatorNode) {
    super({storeInObjectRegistry: false, isPersistent: false});
  }

  get object(): BaseModel { return this.info.obj; }
  get isOpen(): boolean { return this.expanded || this.partial; }

  /** Collapsing also removes the display descendants that would otherwise force the node open. */
  setExpanded(expanded: boolean): void {
    this.expanded = expanded;
    if (!expanded) this.model.removeDisplayDescendants(this);
    this.model.refresh();
    this.raiseOnChange("expanded", undefined, true);
  }

  /** Observes structural changes even when the row is temporarily unmounted. */
  connect(): void {
    if (this.subscription) return;
    this.subscription = this.object.onChange.subscribe((_object, property, source?: string) => {
      if ((property === "expanded" || property === "collapsed") && (this.info.isReference || (source && source !== "navigator"))) return;
      if (property === "expanded") this.setExpanded(true);
      else if (property === "collapsed") this.setExpanded(false);
      else if (!property || property === "childCollection" || property === "@delete" || property === "@undelete") this.model.refresh();
    });
    for (const child of this.cachedChildren.values()) child.connect();
  }

  /** Releases model listeners, including descendants no longer visible in React. */
  dispose(): void {
    if (this.subscription) this.object.onChange.unsubscribe(this.subscription);
    this.subscription = undefined;
    for (const child of this.cachedChildren.values()) child.dispose();
  }
}

/** Owns manual expansion and atomically derives the visible hierarchy from display selections. */
export class NavigatorModel extends BaseObject {
  get className(): string { return "NavigatorModel"; }
  readonly root: NavigatorNode;
  private displayObjects = new Set<BaseModel>();
  private connected = false;
  private refreshing = false;

  constructor(rootObject: BaseModel, private readonly readChildren: NavigatorChildren, displayObjects: readonly BaseModel[] = [],
    private readonly onCollapse?: (object: BaseModel) => void,
    private readonly readUseBreakLines: () => boolean = () => true,
    private readonly readDisplayPath?: (objects: readonly BaseModel[]) => readonly BaseModel[]) {
    super({storeInObjectRegistry: false, isPersistent: false});
    this.displayObjects = new Set(displayObjects);
    this.root = new NavigatorNode(this, new TreeNodeInfo(undefined, rootObject));
    this.root.expanded = true;
    this.refresh(false);
  }

  connect(): void { this.connected = true; this.root.connect(); }
  dispose(): void { this.connected = false; this.root.dispose(); }
  get useBreakLines(): boolean { return this.readUseBreakLines(); }

  /** Replaces the whole path before any node publishes its new children. */
  setDisplayObjects(objects: readonly BaseModel[]): void {
    console.log("[NavigatorDebug] model: setDisplayObjects", {
      root: navigatorDebugObject(this.root.object), connected: this.connected,
      path: objects.map(navigatorDebugObject),
    });
    this.displayObjects = new Set(objects);
    this.refresh();
  }

  /** Explicit collapse resets the entire subtree, including cached, currently hidden descendants. */
  removeDisplayDescendants(node: NavigatorNode): void {
    const removeChildren = (parent: NavigatorNode) => {
      for (const child of parent.cachedChildren.values()) {
        child.expanded = false;
        child.partial = false;
        child.isPartiallyExpanded = false;
        child.hasHiddenPredecessor = false;
        child.hasHiddenSuccessors = false;
        child.visibleChildren = [];
        this.displayObjects.delete(child.object);
        if (child.info.referencedObj) this.displayObjects.delete(child.info.referencedObj);
        removeChildren(child);
      }
    };
    removeChildren(node);
    this.onCollapse?.(node.info.referencedObj ?? node.object);
  }

  /** Reconciles nodes by tree occurrence, retaining manual expansion across temporary hiding. */
  private updateNode(node: NavigatorNode, changed: Set<NavigatorNode>, ancestors: Set<BaseModel>): void {
    if (ancestors.has(node.object)) return;
    const nextAncestors = new Set(ancestors);
    nextAncestors.add(node.object);
    const infos = this.readChildren(node.info);
    const displayPaths = infos.map(info => this.displayObjects.has(info.obj) || (!!info.referencedObj && this.displayObjects.has(info.referencedObj)));
    const hasDisplayPath = displayPaths.some(Boolean);
    const displayedChildren = this.useBreakLines
      ? includeShortSiblingGaps(displayPaths)
      : displayPaths.map(() => hasDisplayPath);
    const available = new Set<string>();
    const visible: NavigatorNode[] = [];
    const occurrences = new Map<string, number>();
    let previousChildHidden = false;
    for (let index = 0; index < infos.length; index++) {
      const info = infos[index];
      const occurrence = occurrences.get(info.obj.id) ?? 0;
      occurrences.set(info.obj.id, occurrence + 1);
      const key = `${info.obj.id}:${info.referencedObj?.id ?? ""}:${occurrence}`;
      available.add(key);
      let child = node.cachedChildren.get(key);
      if (!child) {
        info.parentTreeNodeInfo = node.info;
        child = new NavigatorNode(this, info, node);
        node.cachedChildren.set(key, child);
        if (this.connected) child.connect();
      }
      const onDisplayPath = displayPaths[index];
      const childVisible = node.expanded || displayedChildren[index];
      const hasHiddenPredecessor = childVisible && previousChildHidden;
      if (child.hasHiddenPredecessor !== hasHiddenPredecessor) {
        child.hasHiddenPredecessor = hasHiddenPredecessor;
        changed.add(child);
        // The containing list renders the break before the row.
        changed.add(node);
      }
      previousChildHidden = !childVisible;
      if (childVisible) {
        visible.push(child);
        // Closed branches outside the path need no descendant enumeration.
        if (child.expanded || onDisplayPath) this.updateNode(child, changed, nextAncestors);
        else if (child.partial || child.visibleChildren.length) {
          child.partial = false;
          child.isPartiallyExpanded = false;
          child.hasHiddenSuccessors = false;
          child.visibleChildren = [];
          changed.add(child);
        }
      }
    }
    for (const [key, child] of node.cachedChildren) {
      if (!available.has(key)) { child.dispose(); node.cachedChildren.delete(key); }
    }
    const partial = !node.expanded && visible.length > 0;
    // Automatic opening can already reveal every child (including short gaps).
    // Only show the diamond when expanding would reveal additional rows.
    const isPartiallyExpanded = partial && this.useBreakLines && visible.length < infos.length;
    const hasHiddenSuccessors = visible.length > 0 && previousChildHidden;
    if (node.partial !== partial || node.isPartiallyExpanded !== isPartiallyExpanded || node.hasHiddenSuccessors !== hasHiddenSuccessors || node.visibleChildren.length !== visible.length
      || visible.some((child, index) => node.visibleChildren[index] !== child)) changed.add(node);
    node.partial = partial;
    node.isPartiallyExpanded = isPartiallyExpanded;
    node.hasHiddenSuccessors = hasHiddenSuccessors;
    node.visibleChildren = visible;
    if (changed.has(node) && (node === this.root || this.displayObjects.has(node.object) || hasDisplayPath)) {
      console.log("[NavigatorDebug] model: path node updated", {
        root: navigatorDebugObject(this.root.object), object: navigatorDebugObject(node.object), expanded: node.expanded, partial,
        children: infos.map((info, index) => ({
          object: navigatorDebugObject(info.obj), referenced: navigatorDebugObject(info.referencedObj),
          onDisplayPath: displayPaths[index], visible: node.expanded || displayedChildren[index],
        })),
      });
    }
  }

  /** Publishes only after all affected child lists have reached their final state. */
  refresh(notify = true): void {
    if (this.refreshing) {
      console.log("[NavigatorDebug] model: refresh skipped (already refreshing)", { root: navigatorDebugObject(this.root.object) });
      return;
    }
    this.refreshing = true;
    const changed = new Set<NavigatorNode>();
    try { this.updateNode(this.root, changed, new Set()); }
    finally { this.refreshing = false; }
    if (notify) {
      for (const node of changed) node.raiseOnChange("visibleChildren", undefined, true);
      if (changed.has(this.root)) this.raiseOnChange("roots", undefined, true);
    }
  }

  /** Opens an object's model ancestry without waiting for DOM rows to mount. */
  reveal(object: BaseModel): void {
    const parents = new Set<BaseObject>(this.readDisplayPath?.([object]) ?? []);
    for (let parent = object.parent; parent && !parents.has(parent); parent = parent.parent) parents.add(parent);
    console.log("[NavigatorDebug] model: reveal", {
      root: navigatorDebugObject(this.root.object), object: navigatorDebugObject(object),
      parents: [...parents].map(navigatorDebugObject),
      rootOnPath: object === this.root.object || parents.has(this.root.object),
    });
    const open = (node: NavigatorNode) => {
      if (node !== this.root && !parents.has(node.object)) return;
      node.expanded = true;
      console.log("[NavigatorDebug] model: reveal opens ancestor", { root: navigatorDebugObject(this.root.object), object: navigatorDebugObject(node.object), target: navigatorDebugObject(object) });
      this.refresh();
      for (const child of node.visibleChildren) open(child);
      node.raiseOnChange("expanded", undefined, true);
    };
    open(this.root);
  }
}

/** Allows keyboard navigation to address the node represented by a DOM row. */
export const navigatorRows = new WeakMap<Element, NavigatorNode>();
